/**
 * portal/SsoPage.js — /portal/sso?code=…
 *
 * Arriving from Makua Retreats with the same sign-in. We show who you'll
 * continue as; the first time, you agree to a Forge account being made.
 * Nothing about your Makua bookings comes with you, and a new Forge
 * account starts with no application or standing.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import ForgeLogo from "../components/logo";
import { ssoAPI, setPortalToken, portalAPI } from "./portalApi";
import "./CommunityPortalPage.css";

/* Where to go after signing in: the forum, the forum's own sign-in request, or the dashboard. */
const FORUM_REQUEST = /^\/portal\/community\/connect\?sso=[A-Za-z0-9%+/=_-]+&sig=[0-9a-f]{64}$/;

export default function SsoPage() {
  const [params] = useSearchParams();
  const code = params.get("code") || "";
  const next = params.get("next") || "";
  const toForum = next === "forum" || FORUM_REQUEST.test(next);
  const navigate = useNavigate();
  const [who, setWho] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!code) { setError("This link is incomplete."); return; }
    ssoAPI.peek(code).then((r) => setWho(r.data)).catch((e) => setError(e.message));
  }, [code]);

  const go = async () => {
    setBusy(true); setError("");
    try {
      const r = await ssoAPI.consume(code, true);
      setPortalToken(r.data.token);
      if (next === "forum") {
        const f = await portalAPI.community.info().catch(() => null);
        if (f?.data?.enabled) { window.location.assign(f.data.loginUrl); return; }
        navigate("/portal/community", { replace: true });
      } else if (FORUM_REQUEST.test(next)) {
        navigate(next, { replace: true });
      } else {
        navigate("/portal/dashboard", { replace: true });
      }
    } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div className="cmc">
      <div className="cmc__card">
        <ForgeLogo fill="#b85c38" width={44} height={44} />
        {error ? (
          <>
            <h1 className="cmc__title">This link can't be used</h1>
            <p className="cmc__sub">{error}</p>
            <Link to="/portal/login" className="p-btn p-btn--rust">Sign in to Forge</Link>
          </>
        ) : !who ? (
          <>
            <div className="cmc__spinner" aria-hidden="true" />
            <h1 className="cmc__title">One moment…</h1>
          </>
        ) : (
          <>
            <h1 className="cmc__title">Continue as {who.name || who.email}</h1>
            {who.name && <p className="cmc__sub cmc__email">{who.email}</p>}
            <p className="cmc__sub">
              {who.hasAccount
                ? `You're signed in at ${who.from}. Continue ${toForum ? "to the community" : "to your Forge portal"} with the same sign-in.`
                : toForum
                  ? `The community is shared by ${who.from} and Forge, and signing in to it goes through Forge. Continuing makes a Forge account with your name and email, and nothing else from ${who.from}.`
                  : `You're signed in at ${who.from}. Continuing makes a Forge member account with your name and email, and nothing else from ${who.from}. Applying for a retreat is separate, as always.`}
            </p>
            <button type="button" className="p-btn p-btn--rust" disabled={busy} onClick={go}>
              {busy ? "Signing you in…" : who.hasAccount ? (toForum ? "Continue to the community" : "Continue to Forge") : "Make my Forge account and continue"}
            </button>
            <Link to="/portal/login" className="cmc__alt">Use a different account</Link>
          </>
        )}
      </div>
    </div>
  );
}
